import React, { useState, useEffect } from "react";
import Control from "../common/Control";
import DaySelector from "./DaySelector";
import ApercuRepetition from "./ApercuRepetition";
import { DateTime } from "luxon";
import {
  erreursRecurrence,
  JOURS_OUVRES,
  jourSemaineDe,
  phraseMemeDate,
  rangDansMoisDe,
  RANGS_DANS_MOIS,
} from "./creneauHelpers";

// Les fréquences que l'API connaît (CE-1392). L'ancienne option « Aujourd'hui »
// (toutes les X minutes) n'a jamais été prise en charge : retirée.
const frequences = [
  { id: "DAILY", name: "Tous les jours" },
  { id: "WEEKLY", name: "Toutes les semaines" },
  { id: "MONTHLY", name: "Tous les mois" },
];

// Le refus de l'API sur le rang ou le jour, relié à la liste en cause (`aria-describedby`).
const ID_ERREUR_JOUR_DU_MOIS = "erreur-jour-du-mois";

// `debut` : le jour du créneau, d'où partent la répétition et les choix proposés
// (« le premier mardi », « le 3 du mois »).
const RecurringBox = ({ updateReccuring, slotCalendarConfig, errors, debut }) => {
  const [custom, setCustom] = useState(false);
  const { champs, message } = erreursRecurrence(errors);
  const jourDuCreneau = debut || slotCalendarConfig.start;
  const parRang = (slotCalendarConfig.byRankInMonth || []).length > 0;

  useEffect(() => {
    if (custom) {
      var rec = JSON.parse(JSON.stringify(slotCalendarConfig));
      // En mensuel sur un jour de la semaine, le jour choisi fait la règle : on le garde.
      if (!parRang) rec.byDayOfWeek = [];
      rec.byDayOfMonth = [];
      rec.interval = 1;
      updateReccuring(rec);
    }
  }, [custom]);

  // CE-1416 : « Tous les mois » propose d'abord le jour de la semaine du créneau à
  // son rang dans le mois (le premier mardi), qui tombe toujours un jour ouvré.
  function surJourDeLaSemaine(rec, rang, jourSemaine) {
    rec.byRankInMonth = [rang ?? rangDansMoisDe(jourDuCreneau)];
    rec.byDayOfWeek = [jourSemaine ?? jourSemaineDe(jourDuCreneau)];
    rec.byDayOfMonth = [];
  }

  function updateRec(e) {
    var { name, value } = e.target;

    var rec = JSON.parse(JSON.stringify(slotCalendarConfig));

    if (name == "frequency") {
      if (value == "WEEKLY") {
        rec.byDayOfMonth = [];
      }
      if (value == "MONTHLY") {
        surJourDeLaSemaine(rec);
      } else {
        // Le jour de la semaine venait du mois : gardé, il limiterait « Tous les jours » au mardi.
        if (parRang) rec.byDayOfWeek = [];
        rec.byRankInMonth = [];
      }
      // « Tous les jours » ou aucune répétition : les jours choisis pour une autre répétition (lundi et
      // jeudi, les 5 et 20) n'ont plus de sens, et l'API ne retiendrait qu'eux.
      if (value !== "WEEKLY" && value !== "MONTHLY") {
        rec.byDayOfWeek = [];
        rec.byDayOfMonth = [];
      }
      // Sans répétition, la série s'arrête au jour du créneau : l'API lit une fréquence vide comme
      // quotidienne jusqu'à « Jusqu'au », que l'agent ne voit plus.
      if (value === "") rec.end = new Date(jourDuCreneau).toISOString();
    }
    if (name == "end" && value != "") {
      value = new Date(value).toISOString();
    }
    rec[name] = value;

    updateReccuring(rec);
  }

  function choisirJourDuMois(mode, rang, jourSemaine) {
    var rec = JSON.parse(JSON.stringify(slotCalendarConfig));
    if (mode === "rang") {
      surJourDeLaSemaine(
        rec,
        rang ?? (parRang ? rec.byRankInMonth[0] : undefined),
        jourSemaine ?? (parRang ? rec.byDayOfWeek[0] : undefined)
      );
    } else {
      rec.byRankInMonth = [];
      rec.byDayOfWeek = [];
    }
    updateReccuring(rec);
  }

  const rangAffiche = parRang ? Number(slotCalendarConfig.byRankInMonth[0]) : rangDansMoisDe(jourDuCreneau);
  const jourAffiche = parRang ? slotCalendarConfig.byDayOfWeek[0] || "" : jourSemaineDe(jourDuCreneau);
  const erreurJourSemaine = parRang ? champs.byDayOfWeek : null;
  const erreurJourDuMois = champs.byRankInMonth || erreurJourSemaine;
  const memeDate = phraseMemeDate(slotCalendarConfig, jourDuCreneau);

  return (
    <div>
      <Control
        label="Répéter l'évènement"
        margin="my-2 mt-3"
        name="frequency"
        type="select"
        datas={frequences}
        value={slotCalendarConfig.frequency}
        change={updateRec}
        selectFirstLabel={"Aucune répétition"}
        error={champs}
      />
      {slotCalendarConfig.frequency === "MONTHLY" && (
        <fieldset className="form-group repetition-mois">
          <legend className="repetition-mois__legende">Quel jour du mois ?</legend>
          <label className={"repetition-phrase" + (parRang ? " is-checked" : "")}>
            <input
              type="radio"
              name="repetition-mois"
              value="rang"
              checked={parRang}
              onChange={() => choisirJourDuMois("rang")}
            />
            <span className="repetition-phrase__texte">
              <span className="repetition-phrase__ligne">
                Le
                <select
                  className="form-control repetition-phrase__choix"
                  name="byRankInMonth"
                  aria-label="Rang dans le mois"
                  aria-invalid={champs.byRankInMonth ? "true" : undefined}
                  aria-describedby={champs.byRankInMonth ? ID_ERREUR_JOUR_DU_MOIS : undefined}
                  value={rangAffiche}
                  onChange={(e) => choisirJourDuMois("rang", Number(e.target.value))}
                >
                  {RANGS_DANS_MOIS.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <select
                  className="form-control repetition-phrase__choix"
                  name="byDayOfWeekOfMonth"
                  aria-label="Jour de la semaine"
                  aria-invalid={erreurJourSemaine ? "true" : undefined}
                  aria-describedby={erreurJourSemaine ? ID_ERREUR_JOUR_DU_MOIS : undefined}
                  value={jourAffiche}
                  onChange={(e) => choisirJourDuMois("rang", undefined, e.target.value)}
                >
                  {JOURS_OUVRES.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.name}
                    </option>
                  ))}
                </select>
                du mois
              </span>
              <span className="repetition-phrase__aide">Toujours un jour ouvré, comme les permanences.</span>
            </span>
          </label>
          <label className={"repetition-phrase" + (!parRang ? " is-checked" : "")}>
            <input
              type="radio"
              name="repetition-mois"
              value="date"
              checked={!parRang}
              onChange={() => choisirJourDuMois("date")}
            />
            <span className="repetition-phrase__texte">
              <span className="repetition-phrase__ligne">{memeDate.ligne}</span>
              <span className="repetition-phrase__aide">{memeDate.aide}</span>
            </span>
          </label>
          {erreurJourDuMois && (
            <small id={ID_ERREUR_JOUR_DU_MOIS} role="alert" className="form-text text-danger">
              {erreurJourDuMois}
            </small>
          )}
        </fieldset>
      )}
      {slotCalendarConfig.frequency && (
        <Control
          margin={"my-3"}
          label="Personnaliser la répétition"
          type="checkbox"
          name="personnaliser"
          checked={custom}
          change={(e) => setCustom(e.target.checked)}
        />
      )}
      {custom && (
        <DaySelector
          slotCalendarConfig={slotCalendarConfig}
          updateRec={updateRec}
          errors={champs}
        />
      )}
      {slotCalendarConfig.frequency && (
        <Control
          label="Jusqu'au"
          margin="my-2 mt-3"
          type="date"
          name="end"
          value={DateTime.fromISO(slotCalendarConfig.end).toFormat("yyyy-MM-dd")}
          change={updateRec}
          error={champs}
        />
      )}
      {message && (
        <small role="alert" className="form-text text-danger">
          {message}
        </small>
      )}
      <ApercuRepetition slotCalendarConfig={slotCalendarConfig} debut={jourDuCreneau} />
    </div>
  );
};

export default RecurringBox;
